import React, { useEffect, useMemo } from "react";
import {
  Pagination,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from "flowbite-react";
import { useState } from "react";
import EmptyState from "../EmptyState";

const customTableTheme = {
  root: {
    shadow: "hidden",
    // base: "bg-red-900", // table
    wrapper: "flex-1 flex flex-col border-none",
  },
  // body: {
  //     "base": "bg-red-500",
  //     "wrapper": "bg-red-700"
  // }
};

/**
 * Renders a custom data grid component.
 *
 * @param {Object} props - The properties for the custom data grid.
 * @param {boolean} props.checkboxSelection - Whether to enable checkbox selection. Default is false.
 * @param {string} props.keyId - The key identifier for each row item.
 * @param {function} props.onPageChange - The callback function for page change event. Default logs the page number.
 * @param {boolean} props.isLoading - Whether the data is currently loading. Default is false.
 * @param {Array} props.columns - The array of column objects.
 * @param {string} props.columns[].field - The field name for the column.
 * @param {string} props.columns[].headerName - The header name for the column.
 * @param {string} props.columns[].width - The width of the column.
 * @param {function} props.columns[].renderCell - The function to render the cell content.
 * @param {Array} props.rows - The array of row objects.
 * @param {number} props.pageNumber - The current page number. Default is null.
 * @param {number} props.perPageSize - The number of items per page. Default is 10.
 * @param {number} props.totalPages - The total number of pages. Default is null.
 * @return {JSX.Element} The custom data grid component.
 */
function CustomDataGrid({
  checkboxSelection = false,
  keyId,
  onPageChange = () => {},
  isLoading = false,
  isError = false,
  columns = [],
  rows = [],
  pageNumber = null,
  perPageSize = 10,
  totalPages = null,
  showPagination = true,
  emptyVariant = "first-use",
  emptyTitle,
  emptyDescription,
  emptyAction,
  emptyIcon,
}) {
  const [localPageNumber, setLocalPageNumber] = useState(1);

  useEffect(() => {
    if (pageNumber !== null) {
      setLocalPageNumber(pageNumber);
    }
  }, [pageNumber]);

  const getRows = useMemo(() => {
    return rows?.slice(
      (localPageNumber - 1) * perPageSize,
      localPageNumber * perPageSize
    ).map((row, _i) => {
      return {
        ...row,
        rowIndex: (localPageNumber*perPageSize) - (perPageSize - _i) +1,
      };
    });
  }, [rows, localPageNumber]);

  const isNoData = useMemo(() => {
    return Boolean(!isLoading && rows?.length === 0);
  }, [rows, isLoading]);

  return (
    <div
      // className={`flex-1 flex flex-col overflow-hidden border border-solid mb-12  `}
      className={`flex-1 flex flex-col overflow-hidden border border-solid`}
    >
      <div className={`flex-1 flex flex-col overflow-auto`}>
        <Table
          hoverable={false}
          theme={customTableTheme}
          className="border-none"
        >
          <TableHead
            className={`sticky top-0  bg-[#E8EAEC] border-gray-600  z-[1]`}
          >
            {checkboxSelection && (
              <TableHeadCell className="p-4">
                <Checkbox />
              </TableHeadCell>
            )}
            {columns.map((column, index) => (
              <TableHeadCell
                style={{ minWidth: column?.width ? column?.width : "auto" }}
                // className={`${column?.width ? `min-w-[${column?.width}]` : ''} `}
                className={`p-4 text-[#213650] bg-[#E8EAEC]  normal-case text-sm font-semibold`}
                key={`${column?.field}-${index}`}
              >
                {column?.headerName}
              </TableHeadCell>
            ))}
          </TableHead>
          <TableBody className="divide-y">
            {/* //* Line Loading */}
            {isLoading && (
              <TableRow>
                <TableCell className="p-0 border " colSpan={columns?.length}>
                  <div className="w-full border ">
                    <div className="h-1.5 w-full bg-cyan-100 overflow-hidden">
                      <div className="animate-progress w-full h-full bg-cyan-500 origin-left-right"></div>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            )}

            {/* {!isLoading && rows?.length === 0 && (
              <TableRow>
                <TableCell className="p-0" colSpan={columns?.length}>
                  <div className="w-full">
                    <h1>No Data</h1>
                  </div>
                </TableCell>
              </TableRow>
            )} */}

            {getRows?.map((rowItem, rowIndex) => (
              <TableRow
                className="bg-white dark:border-gray-700 dark:bg-gray-800    text-sm font-medium"
                key={keyId ? rowItem[keyId] : `rowItem-${rowIndex}`}
              >
                {/* CheckBox component */}
                {checkboxSelection && (
                  <TableCell className="p-4 border border-[#E2EBF3]   ">
                    <Checkbox />
                  </TableCell>
                )}

                {/* render cells */}
                {columns.map((columnItem, columnIndex) => {
                  if (columnItem?.renderCell) {
                    // console.log("Rendering cell for row:", rowItem); 
                    return (
                      <TableCell key={`${columnItem?.field}-${columnIndex}`} className="border border-[#E2EBF3] ">
                        {columnItem?.renderCell(
                          rowItem
                        )}
                      </TableCell>
                    );
                  } else {
                    return (
                      <TableCell key={`${columnItem?.field}-${columnIndex}`} className="border border-[#E2EBF3] ">
                        {rowItem[columnItem?.field]}
                      </TableCell>
                    );
                  }
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {isError && !isLoading && (
          <EmptyState
            variant="error"
            title={emptyTitle ?? "Failed to load"}
            description={emptyDescription}
            action={emptyAction}
          />
        )}
        {!isError && isNoData && (
          <EmptyState
            variant={emptyVariant}
            title={emptyTitle}
            description={emptyDescription}
            action={emptyAction}
            icon={emptyIcon}
          />
        )}
      </div>
      {showPagination&&<Pagination
        className={`border-t border-solid border-[#e0e0e0] p-3 flex items-center justify-end`}
        layout="navigation"
        currentPage={localPageNumber}
        totalPages={
          totalPages !== null
            ? totalPages
            : Math.ceil(rows?.length / perPageSize)
        }
        // onPageChange={onPageChange}
        onPageChange={(_page) => {
          if (pageNumber !== null) {
            onPageChange(_page);
          } else {
            setLocalPageNumber(_page);
          }
        }}
        showIcons
        theme={customPaginationTheme}
      />}
      
    </div>
  );
}

export default CustomDataGrid;

const customPaginationTheme = {
  // base: "bg-blue-500",
  pages: {
    base: "xs:mt-0 inline-flex items-center -space-x-px",
  },
};
